export const DEFAULT_COLUMN_WIDTH = 130;

export const MANAGE_COLUMNS_DIALOG_MAX_WIDTH = 'sm';
export const MANAGE_COLUMNS_DIALOG_HEIGHT = 540;

export const getManageColumnsDialogPaperProps = (isMobile: boolean) => ({
  sx: {
    height: isMobile ? undefined : MANAGE_COLUMNS_DIALOG_HEIGHT,
  },
});

export function parseCustomFields(raw: any): Record<string, any> {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch (e) {}
  }
  return {};
}

export function getCustomFieldValue(customFields: any, key: string): any {
  const cf = parseCustomFields(customFields);
  return cf[key] ?? cf[`custom_${key}`] ?? cf[key.replace(/^custom_/, '')];
}

export function hasColumnData(
  leads: any[] | undefined,
  fieldKey: string,
  isCustom: boolean = false
): boolean {
  if (!leads || leads.length === 0) return false;
  return leads.some((row) => {
    let val: any;
    if (isCustom) {
      val = getCustomFieldValue(row.custom_fields, fieldKey);
    } else {
      val = row[fieldKey];
    }
    if (val === undefined || val === null || val === '') return false;
    if (typeof val === 'string' && val.trim() === '') return false;
    if (Array.isArray(val) && val.length === 0) return false;
    return true;
  });
}

export interface RenameItem {
  id?: string;
  label: string;
}

export function validateRenameItem(
  newLabel: string,
  existingItems: RenameItem[],
  currentItemId?: string,
  itemTypeName: string = 'Item'
): string | null {
  const trimmed = newLabel.trim();
  if (!trimmed) {
    return `Please enter a valid ${itemTypeName.toLowerCase()} name.`;
  }

  const normalizedNew = trimmed.toLowerCase();
  for (const item of existingItems) {
    const itemLabelNorm = item.label.trim().toLowerCase();
    if (itemLabelNorm === normalizedNew) {
      if (currentItemId && item.id && item.id === currentItemId) {
        continue;
      }
      return `${itemTypeName} "${trimmed}" already exists.`;
    }
  }

  return null;
}

export function normalizeLabel(label: string): string {
  return (label || '').replace(/\s+/g, '').toLowerCase();
}

export const normalizeOptionLabel = normalizeLabel;

export function resolveOptionLabel(val: any, options: string[] = []): string {
  if (val === undefined || val === null) return '';
  let cleanVal = val;
  if (Array.isArray(cleanVal)) {
    cleanVal = cleanVal[0];
  } else if (typeof cleanVal === 'string' && cleanVal.trim().startsWith('[') && cleanVal.trim().endsWith(']')) {
    try {
      const parsed = JSON.parse(cleanVal);
      if (Array.isArray(parsed)) {
        cleanVal = parsed[0];
      }
    } catch (e) {}
  }
  if (cleanVal === undefined || cleanVal === null) return '';

  if (typeof cleanVal === 'boolean') {
    return cleanVal ? 'Yes' : 'No';
  }

  // 1. Direct Integer Index lookup
  if (typeof cleanVal === 'number' && Number.isInteger(cleanVal) && cleanVal >= 0 && cleanVal < options.length) {
    return options[cleanVal];
  }

  // 2. Direct String Label match or stringified numeric index check
  if (typeof cleanVal === 'string') {
    const match = options.find((o) => o === cleanVal);
    if (match !== undefined) return match;
    const numIdx = Number(cleanVal);
    if (!isNaN(numIdx) && Number.isInteger(numIdx) && numIdx >= 0 && numIdx < options.length) {
      return options[numIdx];
    }
    return cleanVal;
  }

  return String(cleanVal);
}
